import { useState, useCallback, useEffect } from 'react';
import { theme } from 'antd';
import { useSidebarWidth, useSidebarActions } from '../store/store';

const ResizablePanel = ({ children }: { children: React.ReactNode }) => {
  const { token } = theme.useToken();
  const sidebarWidth = useSidebarWidth();
  const { setSidebarWidth } = useSidebarActions();
  const [isDragging, setIsDragging] = useState(false);

  // Start resizing
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  // Handle resize
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;
      const newWidth = e.clientX;
      setSidebarWidth(newWidth);
    },
    [isDragging, setSidebarWidth]
  );

  // End resizing
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Set up and remove event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div
      style={{
        position: 'relative',
        width: `${sidebarWidth}px`,
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid #f0f0f0',
        backgroundColor: token.colorBgContainer,
      }}
      className="palette-sidebar"
    >
      {children}
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '8px',
          height: '100%',
          cursor: 'col-resize',
          zIndex: 10,
          background: isDragging ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
        }}
        onMouseDown={handleMouseDown}
      />
    </div>
  );
};

export default ResizablePanel;
